import React, { useContext, useState, useMemo } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import {
  Box,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Grid,
  Paper,
  Stack,
  IconButton,
  Divider,
  Chip,
  CircularProgress
} from "@mui/material";
import {
  PrecisionManufacturing,
  History,
  EventNote,
  ListAlt,
  BarChart,
  Add,
  Clear,
  WbSunny,
  NightlightRound
} from "@mui/icons-material";

import MyContext from "../../context/MyContext";
import { QK } from "../../base/qk";
import {
  getBuildHistory,
  getScheduledRunVisits,
  getUnAssignedVisits,
  updateVisits,
} from "../../api/services/scheduling";

import DropTask from "./DropTask";
import BuildHistory from "./BuildHistory";
import BuildScheduleModal from "../PopupModal/BuildScheduleModal";
import BuildHistoryModal from "../PopupModal/BuildHistoryModal";
import VisitCallModal from "../PopupModal/VisitCallModal";
import ShowScheduleModal from "../PopupModal/ShowScheduleModal";
import CreateRunModal from "../../screens/ClientsScreen/CreateRunModal";
import Loader from "../Loader/Loader";

import DayTimelineCalendar from "./DayTimelineCalendar";
import { ClientDayTimeline, ClientWeeklyTimeline, ExtendedTimelineView } from "./TimeLine";
import EmployeeCapacityReport from "./EmployeeCapacityReport";

const ItemTypes = { TASK: "card" };

const Job = ({ id, data }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.TASK,
    item: { id, data },
    collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
  }));

  return (
    <Paper
      ref={drag}
      elevation={0}
      sx={{
        p: 1,
        mb: 1.5,
        bgcolor: "#BDBDBD",
        opacity: isDragging ? 0.5 : 1,
        cursor: "move",
        borderRadius: "8px"
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 700, color: "#212121" }}>
        {data.client}, {data.visit_name}
      </Typography>
      <Typography variant="caption" sx={{ display: "block", color: "#424242" }}>
        {new Date(data.start_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} - 
        {new Date(data.end_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
      </Typography>
      {data.visit_type === 2 && (
        <Chip label="COV" size="small" color="success" sx={{ mt: 1, fontWeight: 700, height: 20 }} />
      )}
    </Paper>
  );
};

const TaskDropArea = ({ onDrop, label, tasks }) => {
  const [{ isOver }, drop] = useDrop({
    accept: ItemTypes.TASK,
    drop: (item) => onDrop(item),
    collect: (monitor) => ({ isOver: !!monitor.isOver() }),
  });

  return (
    <Box
      ref={drop}
      sx={{
        p: 2,
        minHeight: "100px",
        borderRadius: "8px",
        bgcolor: isOver ? "#e3f2fd" : "#f5f5f5",
        border: isOver ? "2px dashed #1976d2" : "1px solid #e0e0e0",
        transition: "0.2s"
      }}
    >
      <Typography variant="caption" sx={{ display: "block", mb: 1, textAlign: "center", color: "text.secondary", fontWeight: 700 }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {tasks.map((task) => (
          <Box key={task.id}>
            <DropTask task={task} type="schedule" />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const Scheduling = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { locationID } = useContext(MyContext);

  const [currentView, setCurrentView] = useState("Jobs/Day/Runs");
  const [showBuildModal, setShowBuildModal] = useState(false);
  const [showRunModal, setRunShowModal] = useState(false);
  const [showBuildHistoryModal, setShowBuildHistoryModal] = useState(false);
  const [showVisitCallModal, setShowVisitCallModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedSublocation, setSelectedSublocation] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [runType, setRunType] = useState("");

  const { data: buildHistory } = useQuery(
    [QK.buildHistory, locationID, selectedDate],
    () => getBuildHistory(selectedDate, locationID),
    { enabled: !!locationID }
  );

  const { data: runs, isLoading: runLoading } = useQuery(
    [QK.scheduledRunVisits, selectedDate, selectedSublocation, runType],
    () => getScheduledRunVisits(selectedDate, selectedSublocation, runType),
    { enabled: (!!selectedDate || !!selectedSublocation) && currentView === "Jobs/Day/Runs" }
  );

  const { data: unAssignedTasks } = useQuery(
    [QK.unAssignedTasks, selectedSublocation],
    () => getUnAssignedVisits(selectedSublocation),
    { enabled: !!selectedSublocation }
  );

  const handleDrop = async (task, targetRunId) => {
    try {
      const data = { is_on_right: true, run: targetRunId, date: selectedDate };
      const response = await updateVisits(task.id, data, "schedule");
      if (response.status === 200) {
        enqueueSnackbar("Schedule Job Added", { variant: "success" });
        queryClient.invalidateQueries([QK.scheduledRunVisits]);
        queryClient.invalidateQueries([QK.unAssignedTasks]);
      }
    } catch (error) {
      enqueueSnackbar("Failed to update task", { variant: "error" });
    }
  };

  const isTimelineView = currentView.includes("Timeline") || currentView === "Employee Capacity Report";
  const viewType = useMemo(() => currentView.includes("Week") ? "week" : currentView.includes("Month") ? "month" : "day", [currentView]);

  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ p: 2, bgcolor: "#fff", minHeight: "100vh" }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <BuildHistory
              buildHistory={buildHistory}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              selectedSublocation={selectedSublocation}
              setSelectedSublocation={setSelectedSublocation}
            />
            <Stack direction="row" spacing={1}>
              <Button variant="contained" size="small" startIcon={<PrecisionManufacturing />} onClick={() => setShowBuildModal(true)}>Build</Button>
              <Button variant="contained" size="small" startIcon={<History />} onClick={() => setShowBuildHistoryModal(true)}>History</Button>
              <Button variant="contained" size="small" startIcon={<EventNote />} onClick={() => setShowScheduleModal(true)}>Schedule</Button>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>View Mode</InputLabel>
              <Select value={currentView} onChange={(e) => setCurrentView(e.target.value)} label="View Mode">
                <MenuItem value="Jobs/Day/Runs">Jobs/Day/Runs</MenuItem>
                <MenuItem value="Jobs/Day/Timeline">Jobs/Day/Timeline</MenuItem>
                <MenuItem value="Employee/Day/Timeline">Employee/Day/Timeline</MenuItem>
                <MenuItem value="Employee/Week/Timeline">Employee/Week/Timeline</MenuItem>
                <MenuItem value="Service User/Day/Timeline">Service User/Day/Timeline</MenuItem>
                <MenuItem value="Service User/Week/Timeline">Service User/Week/Timeline</MenuItem>
                <MenuItem value="Employee Capacity Report">Employee Capacity Report</MenuItem>
              </Select>
            </FormControl>
            <Button variant="contained" size="small" startIcon={<ListAlt />}>Actions</Button>
            <Button variant="contained" size="small" startIcon={<BarChart />}>Reports</Button>
          </Stack>
        </Stack>

        <Grid container spacing={2}>
          {!isTimelineView && (
            <Grid item xs={12} md={2}>
              <Paper variant="outlined" sx={{ p: 2, height: "calc(100vh - 160px)", overflowY: "auto", bgcolor: "#fafafa" }}>
                <Typography variant="overline" sx={{ fontWeight: 900, color: "text.secondary" }}>Unassigned</Typography>
                <Box sx={{ mt: 2 }}>
                  {unAssignedTasks?.map((task) => (
                    <Job key={task.id} id={task.id} data={task} />
                  ))}
                </Box>
              </Paper>
            </Grid>
          )}

          <Grid item xs={12} md={isTimelineView ? 12 : 10}>
            <Paper variant="outlined" sx={{ borderRadius: "8px", overflow: "hidden" }}>
              <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, textTransform: "uppercase" }}>Runs</Typography>
                  {currentView === "Jobs/Day/Runs" && (
                    <Stack direction="row" spacing={0.5} sx={{ bgcolor: "#eee", p: 0.5, borderRadius: "4px" }}>
                      <IconButton size="small" onClick={() => setRunShowModal(true)}><Add fontSize="small" /></IconButton>
                      <IconButton size="small" color={runType === "" ? "primary" : "default"} onClick={() => setRunType("")}><Clear fontSize="small" /></IconButton>
                      <IconButton size="small" color={runType === "am" ? "warning" : "default"} onClick={() => setRunType("am")}><WbSunny fontSize="small" /></IconButton>
                      <IconButton size="small" color={runType === "pm" ? "primary" : "default"} onClick={() => setRunType("pm")}><NightlightRound fontSize="small" /></IconButton>
                    </Stack>
                  )}
                </Stack>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "primary.main" }}>
                  {new Date(selectedDate).toLocaleDateString("en-GB", { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
                </Typography>
              </Box>

              <Box sx={{ p: 2, height: "calc(100vh - 220px)", overflowY: "auto" }}>
                {runLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
                ) : (
                  <>
                    {currentView === "Jobs/Day/Runs" && (
                      <Stack spacing={3}>
                        {runs?.map((run, index) => (
                          <Grid container key={index} spacing={2} sx={{ pb: 2, borderBottom: "1px solid #f0f0f0" }}>
                            <Grid item xs={12} md={2.5}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1 }}>
                                {run.run_type === "am" ? <WbSunny sx={{ fontSize: 18, color: "#ffa000" }} /> : <NightlightRound sx={{ fontSize: 18, color: "#303f9f" }} />}
                                {run.name}
                              </Typography>
                              <Box sx={{ mt: 1 }}>
                                {run.employee_names?.map((name, idx) => (
                                  <Typography key={idx} variant="caption" sx={{ display: "block", color: "text.secondary" }}>• {name}</Typography>
                                ))}
                                {(!run.employee_names || run.employee_names.length === 0) && (
                                  <Typography variant="caption" sx={{ fontStyle: "italic", color: "text.disabled" }}>No employees</Typography>
                                )}
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={9.5}>
                              <TaskDropArea onDrop={(task) => handleDrop(task, run.id)} label={`Tasks for ${run.name}`} tasks={run.tasks || []} />
                            </Grid>
                          </Grid>
                        ))}
                      </Stack>
                    )}

                    {currentView === "Jobs/Day/Timeline" && <DayTimelineCalendar selectedDate={selectedDate} setSelectedDate={setSelectedDate} />}
                    {currentView === "Service User/Day/Timeline" && <ClientDayTimeline selectedDate={selectedDate} locationId={locationID} />}
                    {currentView === "Service User/Week/Timeline" && <ClientWeeklyTimeline selectedDate={selectedDate} locationId={locationID} />}
                    {currentView.includes("Employee") && currentView.includes("Timeline") && <ExtendedTimelineView selectedDate={selectedDate} viewType={viewType} />}
                    {currentView === "Employee Capacity Report" && <EmployeeCapacityReport selectedDate={selectedDate} locationId={locationID} />}
                  </>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <BuildScheduleModal show={showBuildModal} handleClose={() => setShowBuildModal(false)} />
        <ShowScheduleModal show={showScheduleModal} handleClose={() => setShowScheduleModal(false)} setDate={setSelectedDate} setSelectedSublocation={setSelectedSublocation} />
        <BuildHistoryModal show={showBuildHistoryModal} setSublocation={setSelectedSublocation} date={setSelectedDate} handleClose={() => setShowBuildHistoryModal(false)} />
        <VisitCallModal show={showVisitCallModal} handleClose={() => setShowVisitCallModal(false)} />
        <CreateRunModal area={selectedSublocation} show={showRunModal} handleClose={() => setRunShowModal(false)} />
      </Box>
    </DndProvider>
  );
};

export default Scheduling;